import { IndexedFilters } from '@/catalog/types/filters';
import FilterPill from './filter-pill';
import parseIndexedFilterForLabel, {
  FilterLabelLookup,
} from '@/catalog/utils/parse-indexed-filter-for-label';
import { useMemo, useState } from 'react';
import Spacer from '@/common/components/spacer';
import { Button } from '@/shadcn-ui/button';
import removeSelectedFilter from '@/catalog/utils/remove-selected-filter';

interface FilterPillsProps {
  selectedFilters: IndexedFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
}

const MAX_DESKTOP_FILTERS_COLLAPSED = 4;

export default function FilterPills({
  selectedFilters,
  setSelectedFilters,
}: FilterPillsProps) {
  const [isDesktopViewCollapsed, setIsDesktopViewCollapsed] = useState(true);

  const filterLabels = useMemo(
    () => parseIndexedFilterForLabel(selectedFilters),
    [selectedFilters]
  );

  const onClick = (filter: FilterLabelLookup) => {
    setSelectedFilters(removeSelectedFilter(filter, selectedFilters, true, true));
  };

  // Separate the base pills (first 4) from the additional pills to allow for smoother animations
  const baseFilters = filterLabels.slice(0, MAX_DESKTOP_FILTERS_COLLAPSED);
  const additionalFilters = filterLabels.slice(MAX_DESKTOP_FILTERS_COLLAPSED);

  return (
    <>
      {/* Mobile view remains unchanged */}
      <div className="relative block md:hidden">
        <div className="scrollbar-hide flex snap-x snap-mandatory gap-[4px] overflow-x-auto">
          {filterLabels.map(({ label, filter }, index) => (
            <FilterPill
              key={`mobile-${index}-${label}`}
              name={label}
              onClickDelegate={() => onClick(filter)}
            />
          ))}
        </div>
        <div className="pointer-events-none absolute right-0 top-0 h-full w-[53px] bg-[linear-gradient(90deg,rgba(244,245,246,0.00)_0%,#F4F5F6_83%)]" />
      </div>

      {/* Desktop view with selective animations */}
      <div className="hidden md:block">
        <div className="flex flex-row flex-wrap gap-[8px]">
          {/* Base filters - no animation */}
          {baseFilters.map(({ label, filter }, index) => (
            <FilterPill
              key={`desktop-base-${index}-${label}`}
              name={label}
              onClickDelegate={() => onClick(filter)}
            />
          ))}
          {!isDesktopViewCollapsed &&
            additionalFilters.map(({ label, filter }, index) => (
              <FilterPill
                key={`desktop-additional-${index}-${label}`}
                name={label}
                onClickDelegate={() => onClick(filter)}
              />
            ))}
        </div>

        {filterLabels.length > MAX_DESKTOP_FILTERS_COLLAPSED && (
          <>
            <Spacer variant="height16" />
            <Button
              type="button"
              variant="clear"
              onClick={() => setIsDesktopViewCollapsed(!isDesktopViewCollapsed)}
              className="h-auto"
            >
              <p className="body3-strong text-flexcar-blue40">
                Show{' '}
                {isDesktopViewCollapsed
                  ? `${filterLabels.length - MAX_DESKTOP_FILTERS_COLLAPSED} more`
                  : 'less'}
              </p>
            </Button>
          </>
        )}
      </div>
    </>
  );
}
